import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { IssueEventType } from 'src/app/models/api/issues/issue-enums.model';
import { DetectionRuleSeverity } from 'src/app/models/api/finding.model';
import { Severities } from 'src/app/components/severities-cell/severities-cell.component';

export interface ITimelineIssueCitation {
  text: string;
  iconPath: string;
}

export interface ITimelineIssueItem {
  date: string;
  time: string;
  text: string; // Rich text in HTML format
  innerText?: string; // Additional details with <red> tags for styling
  iconPath?: string;
  showInnerText?: boolean;
  isLoading?: boolean;
  citations?: ITimelineIssueCitation[];
  tag?: string;

  // Sensitive data properties
  eventId?: string;
  eventType?: IssueEventType;
  sensitiveData?: string;
  showSensitiveData?: boolean;
  isSensitiveDataLoading?: boolean;
  hasSensitiveData?: boolean;

  // Severity for findings/violations (to display severity bars)
  severity?: string;
}

@Component({
  selector: 'app-timeline-issue',
  templateUrl: './timeline-issue.component.html',
  styleUrls: ['./timeline-issue.component.scss'],
  animations: [
    trigger('slideInOutAnimation', [
      state(
        'in',
        style({
          height: '*',
          opacity: 1,
          transform: 'translateY(0)',
        })
      ),
      transition(':enter', [
        style({ height: '0px', opacity: 0, transform: 'translateY(-10px)' }),
        animate('300ms ease-in-out'),
      ]),
      transition(':leave', [
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0, transform: 'translateY(-10px)' })),
      ]),
    ]),
  ],
})
export class TimelineIssueComponent {
  @Input() public timelineIssueItems: ITimelineIssueItem[] = [];
  @Output() public itemClicked = new EventEmitter<ITimelineIssueItem>();

  constructor(private readonly _sanitizer: DomSanitizer) {}

  public isClickableItem(item: ITimelineIssueItem): boolean {
    return item.eventType === IssueEventType.findingAdded || item.eventType === IssueEventType.violationAdded;
  }

  public onItemClick(item: ITimelineIssueItem, event: MouseEvent): void {
    if (this.isClickableItem(item)) {
      event.stopPropagation();
      this.itemClicked.emit(item);
    }
  }

  public parseText(text: string): SafeHtml {
    const parsedText = text.replace(
      /<code>(.*?)<\/code>/g,
      `<span style="background-color:rgb(250, 247, 255); color: #6e32e0;
            padding: 2px 2px; border-radius: 2px; border: 1px solid #6e32e03d; ">$1</span>`
    );
    return this._sanitizer.bypassSecurityTrustHtml(parsedText);
  }

  public toggleInnerText(index: number): void {
    const item = this.timelineIssueItems[index];
    item.showInnerText = !item.showInnerText;

    const delay = this._getRandomDelay(550, 650);

    if (item.showInnerText && item.isLoading === undefined) {
      item.isLoading = true;

      // Simulate HTTP request delay
      setTimeout(() => {
        item.isLoading = false;
      }, delay); // ms delay for loading
    }
  }

  public parseInnerText(innerText: string | undefined): SafeHtml {
    if (!innerText) {
      return '';
    }

    // Replace <red> tags with inline-styled spans
    const parsedText = innerText.replace(
      /<red>(.*?)<\/red>/g,
      `<span style="background-color: #ffe6e6; color: #d9534f;
            padding: 2px 4px; border-radius: 2px; display: inline-block;">$1</span>`
    );
    return this._sanitizer.bypassSecurityTrustHtml(parsedText);
  }

  public toggleSensitiveData(index: number): void {
    const item = this.timelineIssueItems[index];
    if (!item.hasSensitiveData) {
      return;
    }

    item.showSensitiveData = !item.showSensitiveData;
  }

  public parseSensitiveData(sensitiveData: string | undefined): SafeHtml {
    if (!sensitiveData) {
      return '';
    }

    // Apply similar parsing as innerText but for sensitive data
    // Add highlighting for sensitive content
    const parsedText = sensitiveData.replace(
      /<sensitive>(.*?)<\/sensitive>/g,
      `<span style="background-color: #fff3cd; color: #856404;
            padding: 2px 4px; border-radius: 2px; display: inline-block;">$1</span>`
    );

    return this._sanitizer.bypassSecurityTrustHtml(parsedText);
  }

  public expandAllTimelineItems(): void {
    this.timelineIssueItems.forEach((item) => {
      if (item.innerText && !item.showInnerText) {
        item.showInnerText = true;

        if (item.isLoading === undefined) {
          item.isLoading = true;

          // Simulate HTTP request delay
          const delay = this._getRandomDelay(400, 1100);
          setTimeout(() => {
            item.isLoading = false;
          }, delay);
        }
      }
    });
  }

  private _getRandomDelay(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  public getSeveritiesObject(severity: string): Severities {
    return {
      [DetectionRuleSeverity.informational]: severity === DetectionRuleSeverity.informational ? 1 : 0,
      [DetectionRuleSeverity.low]: severity === DetectionRuleSeverity.low ? 1 : 0,
      [DetectionRuleSeverity.medium]: severity === DetectionRuleSeverity.medium ? 1 : 0,
      [DetectionRuleSeverity.high]: severity === DetectionRuleSeverity.high ? 1 : 0,
      [DetectionRuleSeverity.critical]: severity === DetectionRuleSeverity.critical ? 1 : 0,
    };
  }
}
